/**
 * TemplateLibrary — JRNI staff author custom HTML templates.
 *
 * Opened from the Custom HTML templates panel, and only for a session that holds the
 * platform template capability (the server refuses anyone else, whatever this screen
 * shows). A template is an ordinary experience page with `layout: 'custom-html'`; this
 * edits its name, kind, languages, time zone, and one HTML source per language plus one
 * CSS source, all in the SAME saved document. Only custom-html pages are listed; another
 * page is never opened here or converted.
 *
 * Data: bound — listPortalExperiencePages · getPortalExperiencePage · putPortalExperiencePage
 * (Save draft) · publishPortalExperiencePage (Publish) · previewPortalExperiencePage (draft).
 *
 * Rules this screen keeps:
 *  - Save draft changes nothing visitors see; Publish is its own, separate step.
 *  - One mutation at a time. While a save or publish runs, the other is disabled and
 *    switching templates or closing is locked, so what is published is what was saved.
 *  - Every open, new and close starts a new document generation. A late answer for an
 *    earlier document (read, preview, save or publish) is dropped, never shown or merged.
 *  - What a save sent is fingerprinted; edits made while it ran stay "unsaved".
 *  - A new template's address is checked before its first save: never a reserved
 *    address, never one any page already uses, and an unanswerable check refuses.
 *  - "Published" is only said when this session published exactly the saved draft. A
 *    page that was already published when opened says a published version exists.
 *  - A preview that fails or composes nothing keeps the last good page, marked out of
 *    date, and lists what the server found. Publish is held while problems are known.
 *
 * Source editing is the design system's SourceEditor (Find/Replace, Undo/Redo, reviewed
 * formatting, full screen). Components are inserted from the styling contract's own
 * catalogue. There is no visual editing of arbitrary markup.
 */
import React from "react";
import {
  Banner, Button, ConfirmDialog, Dropdown, Field, InlineEmptyState, Input, Modal, SearchPickerDialog, SegmentedControl,
  Skeleton, SourceEditor, Spinner, StatusPill, Tab, Tabs, TimeZonePicker, getSourceEditor, focusSourceEditorRange,
  type SourceDiagnostic,
} from "@v0y4ge/jrni-ui";
import {
  PORTAL_TEMPLATE_COMPONENTS, PORTAL_TEMPLATE_EVENT_COMPONENTS, PortalExperiencePageSlugSchema, PortalLocaleTagSchema,
  validatePortalPageTemplate, type PortalPageKind, type PortalTemplateFinding,
} from "@v0y4ge/api-contracts";
import { t as i18nT } from "../../../lib/i18n";
import { toast } from "../../../shell/toast";
import {
  blankDraft, documentFromDraft, draftFingerprint, draftFromPage, errorFindings, findingsFor, getPage, listAllPages,
  previewPage, publishPage, savePageDraft, slugIsFree, tf, toTemplateError,
  type TemplateCapabilities, type TemplateDraft, type TemplateEventOption,
} from "./template-data";
import { TemplatePreviewFrame } from "./TemplatePreviewFrame";

export interface TemplateLibraryProps {
  open: boolean;
  onClose: () => void;
  initialKind: PortalPageKind;
  /** The full published event catalogue (ended events included), for event-page previews. */
  events: TemplateEventOption[];
  /** The catalogue is still being read. */
  eventsLoading?: boolean;
  /** Why the catalogue couldn't be read, when it couldn't. */
  eventsError?: string | null;
  onRetryEvents?: () => void;
  capabilities: TemplateCapabilities;
}

interface Row { slug: string; title: string; kind: PortalPageKind; published: boolean }
interface RowsState {
  status: "loading" | "error" | "ready";
  data: Row[];
  message?: string;
  /** Unpublished pages not read yet: their layout is only on their own read. */
  unchecked: string[];
  /** Pages whose read failed, so whether they are templates is unknown. */
  unreadable: number;
  checking: boolean;
}
/** Unpublished pages read per batch while looking for draft templates. */
const DRAFT_SCAN_BATCH = 40;
interface Findings { errors: PortalTemplateFinding[]; warnings: PortalTemplateFinding[] }
const NO_FINDINGS: Findings = { errors: [], warnings: [] };

interface PreviewState {
  loading: boolean;
  /** The last page the server composed for this document, kept across failed renders. */
  html: string | null;
  /** The key (document + event + language) `html` was composed for. */
  htmlKey: string | null;
  /** The key of the last answered request, and what the server found for it. */
  answeredKey: string | null;
  findings: Findings;
  error: string | null;
}
const PREVIEW_EMPTY: PreviewState = { loading: false, html: null, htmlKey: null, answeredKey: null, findings: NO_FINDINGS, error: null };

const kindLabel = (k: PortalPageKind) => k === "event-detail"
  ? i18nT("journeys.customTemplates.kindDetail", "Event page")
  : i18nT("journeys.customTemplates.kindList", "Page or event list");

/** Narrow screens show source OR preview, switched; wide ones show both. */
function useNarrow(limit = 1100): boolean {
  const query = `(max-width: ${limit}px)`;
  const get = () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(query).matches;
  const [narrow, setNarrow] = React.useState(get);
  React.useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(query);
    const on = () => setNarrow(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, [query]);
  return narrow;
}

function toDiagnostics(findings: PortalTemplateFinding[], severity: "error" | "warning"): SourceDiagnostic[] {
  return findings.map((f) => ({ line: f.line ?? null, message: f.message, severity, source: f.code }));
}

export function TemplateLibrary(props: TemplateLibraryProps) {
  const narrow = useNarrow();
  const [rows, setRows] = React.useState<RowsState>({ status: "loading", data: [], unchecked: [], unreadable: 0, checking: false });
  const [selected, setSelected] = React.useState<string | null>(null);
  const [doc, setDoc] = React.useState<{ status: "idle" | "loading" | "error" | "ready"; message?: string }>({ status: "idle" });
  const [draft, setDraft] = React.useState<TemplateDraft | null>(null);
  /** Fingerprint of the last draft the server accepted ("" = never saved). */
  const [savedFp, setSavedFp] = React.useState<string>("");
  /** Whether a live version exists, and the fingerprint of it when this session published it. */
  const [live, setLive] = React.useState<{ exists: boolean; fp: string | null }>({ exists: false, fp: null });
  const [tab, setTab] = React.useState<"html" | "css">("html");
  const [activeLocale, setActiveLocale] = React.useState<string>("");
  const [pane, setPane] = React.useState<"source" | "preview">("source");
  const [busy, setBusy] = React.useState<null | "save" | "publish">(null);
  const [serverFindings, setServerFindings] = React.useState<Findings & { message?: string }>(NO_FINDINGS);
  const [preview, setPreview] = React.useState<PreviewState>(PREVIEW_EMPTY);
  const [previewEventId, setPreviewEventId] = React.useState<string>(props.events[0]?.id || "");
  const [pendingLeave, setPendingLeave] = React.useState<null | { then: () => void }>(null);
  const [creating, setCreating] = React.useState<null | { title: string; slug: string; kind: PortalPageKind; locale: string; timeZone: string; checking?: boolean; error?: string }>(null);
  const [inserting, setInserting] = React.useState(false);
  const [newLocale, setNewLocale] = React.useState("");
  const docGen = React.useRef(0);
  const previewSeq = React.useRef(0);
  const rowsSeq = React.useRef(0);
  /** Rows this session's saves put into the list, by address: the list read each one postdates, and
      the server's `updatedAt` for the saved draft. */
  const savedRows = React.useRef(new Map<string, { row: Row; seq: number; updatedAt: string }>());

  const dirty = !!draft && draftFingerprint(draft) !== savedFp;
  const locked = busy !== null;

  /** A new document generation: everything still in flight for the old one is dropped. */
  const nextGeneration = () => {
    docGen.current++;
    previewSeq.current++;
    setPreview(PREVIEW_EMPTY);
    setServerFindings(NO_FINDINGS);
    return docGen.current;
  };

  /* ── Library list ── */
  /** Read one batch of unpublished pages and keep the custom-html ones as draft templates. */
  const scanDrafts = async (slugs: string[]): Promise<{ drafts: Row[]; unreadable: number }> => {
    const drafts: Row[] = [];
    let unreadable = 0;
    for (const slug of slugs) {
      try {
        const page = await getPage(slug);
        if (page.settings.layout === "custom-html") drafts.push({ slug: page.slug, title: page.title, kind: page.settings.pageKind ?? "page", published: false });
      } catch { unreadable++; }
    }
    return { drafts, unreadable };
  };
  const sortRows = (list: Row[]) => [...list].sort((a, b) => a.title.localeCompare(b.title));
  /** The list with one row in place of any row with the same address, in title order. */
  const withRow = (list: Row[], row: Row) => sortRows([...list.filter((x) => x.slug !== row.slug), row]);
  const loadRows = React.useCallback(async () => {
    const seq = ++rowsSeq.current;
    setRows((r) => ({ ...r, status: "loading", checking: false }));
    try {
      const pages = await listAllPages();
      /* Published custom-html pages are known from the list. An unpublished page's layout
         is only on its own read, so those are read in batches; the rest stay listed as
         "not checked yet" with a control to check them, never silently left out. */
      const liveRows: Row[] = pages.filter((p) => p.liveLayout === "custom-html")
        .map((p) => ({ slug: p.slug, title: p.title, kind: (p.livePageKind ?? "page") as PortalPageKind, published: true }));
      const unpublished = pages.filter((x) => x.liveLayout !== "custom-html" && !x.published).map((x) => x.slug);
      const first = await scanDrafts(unpublished.slice(0, DRAFT_SCAN_BATCH));
      if (seq !== rowsSeq.current) return;
      /* A saved row stays while nothing newer is known for its page. A read that began before
         the save is older than it. A later read still describes the same saved draft when its
         summary carries the saved `updatedAt`: a published page's list title is its live
         title, not the draft's. A later read with another `updatedAt`, or without the page,
         is newer, so the saved row is dropped. */
      const updatedAtBySlug = new Map(pages.map((p) => [p.slug, p.updatedAt] as const));
      let data = sortRows([...liveRows, ...first.drafts]);
      let unchecked = unpublished.slice(DRAFT_SCAN_BATCH);
      for (const [slug, saved] of savedRows.current) {
        if (saved.seq < seq && updatedAtBySlug.get(slug) !== saved.updatedAt) { savedRows.current.delete(slug); continue; }
        data = withRow(data, saved.row);
        unchecked = unchecked.filter((s) => s !== slug);
      }
      setRows({ status: "ready", data, unchecked, unreadable: first.unreadable, checking: false });
    } catch (err) {
      if (seq === rowsSeq.current) setRows({ status: "error", data: [], message: toTemplateError(err).message, unchecked: [], unreadable: 0, checking: false });
    }
  }, []);
  const checkMore = async () => {
    if (rows.checking || !rows.unchecked.length) return;
    const seq = rowsSeq.current;
    const batch = rows.unchecked.slice(0, DRAFT_SCAN_BATCH);
    setRows((r) => ({ ...r, checking: true }));
    const found = await scanDrafts(batch);
    if (seq !== rowsSeq.current) return;
    setRows((r) => ({
      ...r, checking: false, data: sortRows([...r.data, ...found.drafts.filter((d) => !r.data.some((x) => x.slug === d.slug))]),
      unchecked: r.unchecked.filter((s) => !batch.includes(s)), unreadable: r.unreadable + found.unreadable,
    }));
  };
  React.useEffect(() => { if (props.open) loadRows(); }, [props.open, loadRows]);

  /* Leaving with unsaved changes always asks; nothing can leave mid-save or mid-publish. */
  React.useEffect(() => {
    if (!dirty && !locked) return;
    const onBefore = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onBefore);
    return () => window.removeEventListener("beforeunload", onBefore);
  }, [dirty, locked]);
  const guard = (then: () => void) => { if (locked) return; if (dirty) setPendingLeave({ then }); else then(); };

  const resetToIdle = () => {
    nextGeneration();
    setSelected(null);
    setDraft(null);
    setSavedFp("");
    setLive({ exists: false, fp: null });
    setDoc({ status: "idle" });
  };
  const close = () => guard(() => { resetToIdle(); props.onClose(); });

  /* ── Open one template ── */
  const openTemplate = (slug: string) => {
    const gen = nextGeneration();
    setSelected(slug);
    setDoc({ status: "loading" });
    setDraft(null);
    setSavedFp("");
    getPage(slug).then((page) => {
      if (gen !== docGen.current) return;
      if (page.settings.layout !== "custom-html") {
        setDoc({ status: "error", message: i18nT("journeys.customTemplates.notTemplate", "This page isn't a custom HTML template, so it can't be edited here.") });
        return;
      }
      const d = draftFromPage(page);
      setDraft(d);
      setSavedFp(draftFingerprint(d));
      setLive({ exists: page.published, fp: null });
      setActiveLocale(d.locale);
      setTab("html");
      setDoc({ status: "ready" });
    }).catch((err) => { if (gen === docGen.current) setDoc({ status: "error", message: toTemplateError(err).message }); });
  };

  /* ── Local validation (the same validator the server runs) ── */
  const local: Findings = React.useMemo(() => {
    if (!draft) return NO_FINDINGS;
    const body = documentFromDraft(draft);
    return validatePortalPageTemplate(body.template, body.blocks, {
      defaultLocale: draft.locale, supportedLocales: draft.supportedLocales, pageKind: draft.kind,
    } as Parameters<typeof validatePortalPageTemplate>[2]);
  }, [draft]);

  /* ── Draft preview: composed by the server, never by this screen ── */
  const previewEvent = props.events.find((e) => e.id === previewEventId) || props.events[0] || null;
  const previewKey = draft ? draftFingerprint(draft) + "|" + (draft.kind === "event-detail" ? previewEvent?.slug ?? "" : "") + "|" + activeLocale : "";
  const runPreview = (d: TemplateDraft, key: string) => {
    const gen = docGen.current;
    const seq = ++previewSeq.current;
    setPreview((p) => ({ ...p, loading: true, error: null }));
    previewPage(d.slug, {
      mode: "draft", document: documentFromDraft(d), locale: activeLocale || d.locale,
      ...(d.kind === "event-detail" && previewEvent ? { eventSlug: previewEvent.slug } : {}),
    }).then((result) => {
      if (gen !== docGen.current || seq !== previewSeq.current) return;
      setPreview((p) => ({
        loading: false, error: null, answeredKey: key, findings: { errors: result.errors, warnings: result.warnings },
        html: result.html ?? p.html, htmlKey: result.html ? key : p.htmlKey,
      }));
    }).catch((err) => {
      if (gen !== docGen.current || seq !== previewSeq.current) return;
      const e = toTemplateError(err);
      setPreview((p) => ({ ...p, loading: false, answeredKey: key, error: e.message, findings: { errors: errorFindings(e), warnings: [] } }));
    });
  };
  React.useEffect(() => {
    if (!draft || local.errors.length) return;
    if (draft.kind === "event-detail" && !previewEvent) return;
    if (preview.answeredKey === previewKey) return;
    const d = draft;
    const key = previewKey;
    const timer = window.setTimeout(() => runPreview(d, key), 900);
    return () => window.clearTimeout(timer);
  }, [previewKey, local.errors.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const previewCurrent = !!preview.html && preview.htmlKey === previewKey;
  const previewProblems = preview.answeredKey === previewKey ? preview.findings.errors : [];
  const previewWarnings = preview.answeredKey === previewKey ? preview.findings.warnings : [];
  const staleNote = !preview.html ? null
    : local.errors.length ? i18nT("journeys.customTemplates.previewPaused", "Preview paused: fix the highlighted problems to update it.")
      : preview.answeredKey === previewKey && !previewCurrent ? i18nT("journeys.customTemplates.previewNotUpdated", "This preview is out of date: the latest version couldn't be shown. See the problems listed.")
        : !previewCurrent ? i18nT("journeys.customTemplates.previewOutOfDate", "Preview will update in a moment.") : null;

  /* ── Edits ── */
  const update = (patch: Partial<TemplateDraft>) => setDraft((d) => (d ? { ...d, ...patch } : d));
  const setSource = (text: string) => {
    if (!draft) return;
    if (tab === "css") update({ css: text });
    else update({ html: { ...draft.html, [activeLocale]: text } });
  };
  const sourceId = `tpl-src-${draft?.slug ?? "none"}-${tab}`;
  const sourceValue = !draft ? "" : tab === "css" ? draft.css : (draft.html[activeLocale] ?? "");

  const insertComponent = (tag: string) => {
    setInserting(false);
    if (!draft || tab !== "html") return;
    const snippet = `<${tag}></${tag}>`;
    const ed = getSourceEditor(sourceId);
    const sel = ed ? ed.getSelection() : { from: sourceValue.length, to: sourceValue.length };
    const next = sourceValue.slice(0, sel.from) + snippet + sourceValue.slice(sel.to);
    update({ html: { ...draft.html, [activeLocale]: next } });
    window.setTimeout(() => focusSourceEditorRange(sourceId, sel.from + tag.length + 2), 0);
  };
  const catalog = [
    ...PORTAL_TEMPLATE_COMPONENTS.map((c) => ({ tag: c.tag, purpose: c.purpose })),
    ...(draft?.kind === "event-detail" ? PORTAL_TEMPLATE_EVENT_COMPONENTS.map((c) => ({ tag: c.tag, purpose: c.purpose })) : []),
  ];

  const localeValid = (tag: string) => PortalLocaleTagSchema.safeParse(tag).success;
  const addLocale = () => {
    if (!draft) return;
    const tag = newLocale.trim();
    if (!localeValid(tag) || draft.supportedLocales.includes(tag)) return;
    update({ supportedLocales: [...draft.supportedLocales, tag], html: { ...draft.html, [tag]: draft.html[draft.locale] ?? "" } });
    setActiveLocale(tag);
    setNewLocale("");
  };

  /* ── Save draft / publish (one at a time, scoped to the document generation) ── */
  const save = async () => {
    if (!draft || busy) return;
    const gen = docGen.current;
    const sent = draft;
    const fp = draftFingerprint(sent);
    setBusy("save");
    setServerFindings(NO_FINDINGS);
    try {
      if (!sent.base) {
        /* First save of a new template: the address must still be free right now. */
        const free = await slugIsFree(sent.slug);
        if (free !== "free") {
          if (gen === docGen.current) setServerFindings({ ...NO_FINDINGS, message: free === "reserved"
            ? i18nT("journeys.customTemplates.slugReserved", "That address is reserved. Start a new template with another address.")
            : i18nT("journeys.customTemplates.slugTakenNow", "Another page now uses that address, so this template wasn't saved. Start a new template with another address.") });
          return;
        }
      }
      const page = await savePageDraft(sent.slug, documentFromDraft(sent));
      if (gen !== docGen.current) return;
      /* Keep anything typed while the save ran: only the stored base (and the blocks the
         server derived from the template) is taken from the answer. */
      setDraft((cur) => (cur && cur.slug === sent.slug ? { ...cur, base: page } : cur));
      setSavedFp(fp);
      setLive((l) => ({ exists: page.published, fp: l.fp }));
      /* The list shows the saved name straight away. `published` is the server's word on
         whether a live version exists; saving a draft never makes it true. */
      if (page.settings.layout === "custom-html") {
        const row: Row = { slug: page.slug, title: page.title, kind: page.settings.pageKind ?? "page", published: page.published };
        savedRows.current.set(row.slug, { row, seq: rowsSeq.current, updatedAt: page.updatedAt });
        setRows((r) => ({ ...r, data: withRow(r.data, row), unchecked: r.unchecked.filter((s) => s !== row.slug) }));
      }
      toast({ tone: "success", title: i18nT("journeys.customTemplates.saved", "Draft saved"), subtitle: i18nT("journeys.customTemplates.savedHint", "Visitors don't see it until you publish.") });
    } catch (err) {
      if (gen !== docGen.current) return;
      const e = toTemplateError(err);
      setServerFindings({ errors: errorFindings(e), warnings: [], message: e.message });
    } finally {
      if (gen === docGen.current) setBusy(null);
    }
  };
  const publish = async () => {
    if (!draft || busy || dirty || !savedFp) return;
    const gen = docGen.current;
    const fp = savedFp;
    const slug = draft.slug;
    setBusy("publish");
    setServerFindings(NO_FINDINGS);
    try {
      const { page, warnings } = await publishPage(slug);
      if (gen !== docGen.current) return;
      setDraft((cur) => (cur && cur.slug === slug ? { ...cur, base: page } : cur));
      setLive({ exists: true, fp });
      setServerFindings({ errors: [], warnings });
      /* What was saved is now live, so the list's own read describes it. */
      savedRows.current.delete(slug);
      toast({
        tone: "success", title: i18nT("journeys.customTemplates.published", "Template published"),
        subtitle: warnings.length ? i18nT("journeys.customTemplates.publishedWarnings", "Published, with warnings to review.") : undefined,
      });
      loadRows();
    } catch (err) {
      if (gen !== docGen.current) return;
      const e = toTemplateError(err);
      setServerFindings({ errors: errorFindings(e), warnings: [], message: e.message });
    } finally {
      if (gen === docGen.current) setBusy(null);
    }
  };

  /* ── New template ── */
  const createTemplate = async () => {
    if (!creating || creating.checking) return;
    const input = creating;
    const slug = input.slug.trim();
    const fail = (error: string) => setCreating((c) => (c ? { ...c, checking: false, error } : c));
    if (!input.title.trim()) return fail(i18nT("journeys.customTemplates.needTitle", "Give the template a name."));
    if (!PortalExperiencePageSlugSchema.safeParse(slug).success) return fail(i18nT("journeys.customTemplates.badSlug", "Use lowercase letters, numbers and hyphens for the address."));
    if (!localeValid(input.locale.trim())) return fail(i18nT("journeys.customTemplates.badLocale", "Use a language tag such as en or en-US."));
    setCreating({ ...input, checking: true, error: undefined });
    try {
      const free = await slugIsFree(slug);
      if (free === "reserved") return fail(i18nT("journeys.customTemplates.slugReservedCreate", "That address is reserved. Choose another."));
      if (free === "taken") return fail(i18nT("journeys.customTemplates.slugTaken", "Another page already uses that address. Choose another."));
    } catch (err) {
      return fail(tf("journeys.customTemplates.slugCheckFailed", "The address couldn't be checked, so nothing was created: {reason}", { reason: toTemplateError(err).message }));
    }
    const gen = nextGeneration();
    if (gen !== docGen.current) return;
    const d = blankDraft(slug, input.title.trim(), input.kind, input.locale.trim(), input.timeZone);
    setCreating(null);
    setSelected(d.slug);
    setDraft(d);
    setSavedFp(""); // a new template is unsaved until its first save
    setLive({ exists: false, fp: null });
    setActiveLocale(d.locale);
    setTab("html");
    setDoc({ status: "ready" });
  };

  /* ── Render ── */
  const findingsHere = draft ? [
    ...findingsFor(local.errors, tab, activeLocale).map((f) => ({ ...f, sev: "error" as const })),
    ...findingsFor([...serverFindings.errors, ...previewProblems], tab, activeLocale).map((f) => ({ ...f, sev: "error" as const })),
    ...findingsFor([...local.warnings, ...serverFindings.warnings, ...previewWarnings], tab, activeLocale).map((f) => ({ ...f, sev: "warning" as const })),
  ] : [];
  const diagnostics: SourceDiagnostic[] = [
    ...toDiagnostics(findingsHere.filter((f) => f.sev === "error"), "error"),
    ...toDiagnostics(findingsHere.filter((f) => f.sev === "warning"), "warning"),
  ];
  const allErrors = [...local.errors, ...serverFindings.errors, ...previewProblems];
  const otherErrors = allErrors.filter((f, i) => !findingsHere.some((h) => h.path === f.path && h.message === f.message)
    && allErrors.findIndex((g) => g.path === f.path && g.message === f.message) === i);
  const knownProblems = local.errors.length > 0 || previewProblems.length > 0;
  const allWarnings = [...serverFindings.warnings, ...previewWarnings]
    .filter((f, i, list) => list.findIndex((g) => g.path === f.path && g.message === f.message) === i);

  const statusPill = !draft ? null
    : dirty ? <StatusPill tone="warning">{savedFp ? i18nT("journeys.customTemplates.unsaved", "Unsaved changes") : i18nT("journeys.customTemplates.notSavedYet", "New, not saved yet")}</StatusPill>
      : live.fp !== null && live.fp === savedFp ? <StatusPill tone="success">{i18nT("journeys.customTemplates.statusPublished", "Published")}</StatusPill>
        : live.exists && live.fp !== null ? <StatusPill tone="warning">{i18nT("journeys.customTemplates.statusDiffers", "Saved draft, differs from the published version")}</StatusPill>
          : live.exists ? <StatusPill tone="info">{i18nT("journeys.customTemplates.statusLiveExists", "Saved draft · a published version exists")}</StatusPill>
            : <StatusPill tone="neutral">{i18nT("journeys.customTemplates.statusDraft", "Saved draft, not published")}</StatusPill>;

  const listPane = (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px", minWidth: 0 }}>
      <Button hierarchy="secondary" size="sm" disabled={locked}
        onClick={() => guard(() => setCreating({ title: "", slug: "", kind: props.initialKind, locale: draft?.locale || "en", timeZone: draft?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone }))}>
        {i18nT("journeys.customTemplates.newTemplate", "New template")}
      </Button>
      {rows.status === "loading" ? (
        <div role="status" aria-label={i18nT("journeys.customTemplates.loading", "Loading templates")} style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rect" width="100%" height={52} />)}
        </div>
      ) : rows.status === "error" ? (
        <Banner tone="danger" size="compact" action={<Button size="sm" hierarchy="secondary" onClick={loadRows}>{i18nT("journeys.customTemplates.retry", "Try again")}</Button>}>{rows.message}</Banner>
      ) : rows.data.length === 0 && !rows.unchecked.length ? (
        <InlineEmptyState size="sm" title={i18nT("journeys.customTemplates.libraryEmpty", "No templates yet")} description={i18nT("journeys.customTemplates.libraryEmptyHint", "Create one to get started.")} />
      ) : (
        <div role="listbox" aria-label={i18nT("journeys.customTemplates.templates", "Templates")} style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          {rows.data.map((r) => (
            <button key={r.slug} type="button" role="option" aria-selected={selected === r.slug} disabled={locked}
              onClick={() => guard(() => openTemplate(r.slug))}
              style={{
                textAlign: "left", display: "flex", flexDirection: "column", gap: "4px", padding: "10px 12px", cursor: locked ? "not-allowed" : "pointer",
                borderRadius: "var(--jrni-radius-md)", font: "inherit", color: "var(--jrni-color-text-strong)", opacity: locked && selected !== r.slug ? 0.6 : 1,
                border: "1px solid " + (selected === r.slug ? "var(--jrni-color-primary-1)" : "var(--jrni-color-surface-border)"),
                background: selected === r.slug ? "var(--jrni-color-primary-4)" : "var(--jrni-color-surface-1, transparent)",
              }}>
              <span style={{ fontWeight: 600, fontSize: "13px", overflowWrap: "anywhere" }}>{r.title}</span>
              <span style={{ display: "flex", gap: "6px", flexWrap: "wrap", alignItems: "center", fontSize: "12px", color: "var(--jrni-color-text-muted)" }}>
                <span>{kindLabel(r.kind)}</span>
                <StatusPill size="sm" tone={r.published ? "success" : "neutral"}>{r.published ? i18nT("journeys.customTemplates.hasPublished", "Has a published version") : i18nT("journeys.customTemplates.statusDraftShort", "Draft")}</StatusPill>
              </span>
            </button>
          ))}
        </div>
      )}
      {rows.status === "ready" && rows.unchecked.length ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <span style={{ fontSize: "12px", color: "var(--jrni-color-text-muted)" }}>
            {tf("journeys.customTemplates.uncheckedPages", "{count, plural, one {# unpublished page not checked yet} other {# unpublished pages not checked yet}}", { count: rows.unchecked.length })}
          </span>
          <Button size="sm" hierarchy="tertiary" disabled={rows.checking} aria-busy={rows.checking || undefined}
            iconLeft={rows.checking ? <Spinner size="sm" label={i18nT("journeys.customTemplates.checking", "Checking")} /> : undefined}
            onClick={() => { void checkMore(); }}>
            {rows.checking ? i18nT("journeys.customTemplates.checking", "Checking") : i18nT("journeys.customTemplates.checkMore", "Check more pages")}
          </Button>
        </div>
      ) : null}
      {rows.status === "ready" && rows.unreadable ? (
        <span style={{ fontSize: "12px", color: "var(--jrni-color-text-muted)" }}>
          {tf("journeys.customTemplates.unreadablePages", "{count, plural, one {# page couldn't be read} other {# pages couldn't be read}}", { count: rows.unreadable })}
        </span>
      ) : null}
    </div>
  );

  let editor: React.ReactNode;
  if (doc.status === "idle") {
    editor = <InlineEmptyState title={i18nT("journeys.customTemplates.pickOne", "Choose a template")} description={i18nT("journeys.customTemplates.pickOneHint", "Pick one from the list, or create a new one.")} />;
  } else if (doc.status === "error") {
    editor = (
      <Banner tone="danger" title={i18nT("journeys.customTemplates.openFailed", "This template couldn't be opened")}
        action={selected ? <Button size="sm" hierarchy="secondary" onClick={() => openTemplate(selected)}>{i18nT("journeys.customTemplates.retry", "Try again")}</Button> : undefined}>{doc.message}</Banner>
    );
  } else if (doc.status === "loading" || !draft) {
    editor = (
      <div role="status" aria-label={i18nT("journeys.customTemplates.opening", "Opening template")} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        <Skeleton variant="rect" width="100%" height={40} />
        <Skeleton variant="rect" width="100%" height={36} />
        <Skeleton variant="rect" width="100%" height={420} />
      </div>
    );
  } else {
    const sourcePane = (
      <div style={{ display: "flex", flexDirection: "column", gap: "10px", minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "flex-end", gap: "8px", flexWrap: "wrap" }}>
          <Field label={i18nT("journeys.customTemplates.language", "Language")} style={{ minWidth: "140px" }}>
            <Dropdown size="sm" value={activeLocale} onChange={setActiveLocale}
              aria-label={i18nT("journeys.customTemplates.language", "Language")}
              options={draft.supportedLocales.map((l) => ({ value: l, label: l, supportingText: l === draft.locale ? i18nT("journeys.customTemplates.defaultLanguage", "Default") : undefined }))} />
          </Field>
          <Field label={i18nT("journeys.customTemplates.addLanguage", "Add a language")} hint={i18nT("journeys.customTemplates.addLanguageHint", "A language tag, such as en or es-MX")} style={{ minWidth: "160px" }}>
            <div style={{ display: "flex", gap: "6px" }}>
              <Input size="sm" value={newLocale} onChange={(e) => setNewLocale(e.target.value)} aria-label={i18nT("journeys.customTemplates.addLanguage", "Add a language")} />
              <Button size="sm" hierarchy="secondary" disabled={!localeValid(newLocale.trim()) || draft.supportedLocales.includes(newLocale.trim())} onClick={addLocale}>
                {i18nT("journeys.customTemplates.add", "Add")}
              </Button>
            </div>
          </Field>
          <Button size="sm" hierarchy="secondary" onClick={() => setInserting(true)} disabled={tab !== "html" || !props.capabilities.author}>
            {i18nT("journeys.customTemplates.insertComponent", "Insert component")}
          </Button>
        </div>
        <Tabs value={tab} onChange={(v) => setTab(v as "html" | "css")} idBase={`tpl-tabs-${draft.slug}`}>
          <Tab value="html">HTML</Tab>
          <Tab value="css">CSS</Tab>
        </Tabs>
        <SourceEditor
          id={sourceId}
          docKey={`${draft.slug}:${tab === "css" ? "css" : activeLocale}:${tab}`}
          label={tab === "css" ? i18nT("journeys.customTemplates.cssSource", "CSS (all languages)") : tf("journeys.customTemplates.htmlSource", "HTML for {locale}", { locale: activeLocale })}
          language={tab === "css" ? "css" : "liquid-html"}
          value={sourceValue}
          onChange={setSource}
          rows={22}
          maxHeight="calc(100vh - 360px)"
          diagnostics={diagnostics}
          invalid={diagnostics.some((d) => d.severity === "error")}
          readOnly={!props.capabilities.author}
        />
      </div>
    );
    const previewPane = (
      <div style={{ display: "flex", flexDirection: "column", gap: "10px", minWidth: 0 }}>
        {draft.kind === "event-detail" && props.eventsError ? (
          <Banner tone="danger" size="compact" title={i18nT("journeys.customTemplates.catalogFailed", "Events couldn't be loaded")}
            action={props.onRetryEvents ? <Button size="sm" hierarchy="secondary" onClick={props.onRetryEvents}>{i18nT("journeys.customTemplates.retry", "Try again")}</Button> : undefined}>
            {props.eventsError}
          </Banner>
        ) : null}
        {draft.kind === "event-detail" && props.eventsLoading && !props.events.length ? (
          <div role="status" aria-label={i18nT("journeys.customTemplates.loadingEvents", "Loading events…")}>
            <Skeleton variant="rect" width="100%" height={36} />
          </div>
        ) : draft.kind === "event-detail" && props.eventsError && !props.events.length ? null : draft.kind === "event-detail" ? (
          props.events.length ? (
            <Field label={i18nT("journeys.customTemplates.previewWithEvent", "Preview with event")}>
              <Dropdown size="sm" searchable value={previewEvent?.id ?? null} onChange={setPreviewEventId}
                aria-label={i18nT("journeys.customTemplates.previewWithEvent", "Preview with event")}
                options={props.events.map((e) => ({ value: e.id, label: e.name, supportingText: e.when }))} />
            </Field>
          ) : (
            <Banner tone="info" size="compact">{i18nT("journeys.customTemplates.previewNeedsEvent", "A preview needs a published event.")}</Banner>
          )
        ) : null}
        {previewProblems.length ? (
          <Banner tone="danger" size="compact" title={i18nT("journeys.customTemplates.previewFindings", "The preview found problems")}>
            <ul style={{ margin: 0, paddingLeft: "18px" }}>{previewProblems.slice(0, 8).map((f, i) => <li key={i}>{f.message}</li>)}</ul>
          </Banner>
        ) : null}
        <TemplatePreviewFrame html={preview.html} loading={preview.loading} error={preview.error} staleNote={staleNote}
          onRetry={() => runPreview(draft, previewKey)} title={i18nT("journeys.customTemplates.previewFrame", "Template preview")}
          emptyTitle={local.errors.length ? i18nT("journeys.customTemplates.fixFirst", "Fix the highlighted problems to see a preview") : i18nT("journeys.customTemplates.previewSoon", "The preview appears here")}
          height="calc(100vh - 330px)" />
      </div>
    );
    editor = (
      <div style={{ display: "flex", flexDirection: "column", gap: "12px", minWidth: 0, minHeight: 0 }}>
        <div style={{ display: "flex", alignItems: "flex-end", gap: "10px", flexWrap: "wrap" }}>
          <Field label={i18nT("journeys.customTemplates.templateName", "Template name")} style={{ flex: "1 1 220px", minWidth: 0 }}
            hint={props.capabilities.author ? i18nT("journeys.customTemplates.renameHint", "Renaming keeps the address and any event assignments.") : undefined}>
            <Input value={draft.title} onChange={(e) => update({ title: e.target.value })} full maxLength={200} readOnly={!props.capabilities.author}
              aria-label={i18nT("journeys.customTemplates.templateName", "Template name")} />
          </Field>
          <Field label={i18nT("journeys.customTemplates.kind", "Used for")}>
            <SegmentedControl size="sm" value={draft.kind} onChange={(v) => { if (props.capabilities.author) update({ kind: v as PortalPageKind }); }}
              ariaLabel={i18nT("journeys.customTemplates.kind", "Used for")}
              options={[{ value: "page", label: kindLabel("page") }, { value: "event-detail", label: kindLabel("event-detail") }]} />
          </Field>
          <Field label={i18nT("journeys.customTemplates.timeZone", "Time zone")} style={{ minWidth: "200px" }}>
            <TimeZonePicker size="sm" value={draft.timeZone} onChange={(z) => update({ timeZone: z })} disabled={!props.capabilities.author} />
          </Field>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", fontSize: "12px", color: "var(--jrni-color-text-muted)" }}>
          <span>/{draft.slug}</span>
          {statusPill}
          <span style={{ flex: "1 1 auto" }} />
          <Button size="sm" hierarchy="secondary" disabled={!props.capabilities.author || !dirty || locked}
            aria-busy={busy === "save" || undefined} iconLeft={busy === "save" ? <Spinner size="sm" label={i18nT("journeys.customTemplates.saving", "Saving")} /> : undefined}
            onClick={() => { void save(); }}>
            {busy === "save" ? i18nT("journeys.customTemplates.saving", "Saving") : i18nT("journeys.customTemplates.saveDraft", "Save draft")}
          </Button>
          <Button size="sm" hierarchy="primary" disabled={!props.capabilities.publish || dirty || !savedFp || locked || knownProblems}
            aria-busy={busy === "publish" || undefined} iconLeft={busy === "publish" ? <Spinner size="sm" onColor label={i18nT("journeys.customTemplates.publishing", "Publishing")} /> : undefined}
            onClick={() => { void publish(); }}>
            {busy === "publish" ? i18nT("journeys.customTemplates.publishing", "Publishing") : i18nT("journeys.customTemplates.publish", "Publish template")}
          </Button>
        </div>
        {dirty && savedFp ? (
          <span style={{ fontSize: "12px", color: "var(--jrni-color-text-muted)" }}>{i18nT("journeys.customTemplates.saveBeforePublish", "Save the draft before publishing. Publishing puts the saved draft live.")}</span>
        ) : null}
        {knownProblems && !dirty && savedFp ? (
          <span style={{ fontSize: "12px", color: "var(--jrni-color-text-muted)" }}>{i18nT("journeys.customTemplates.fixBeforePublish", "Fix the problems listed before publishing.")}</span>
        ) : null}
        {serverFindings.message ? (
          <Banner tone="danger" size="compact" title={i18nT("journeys.customTemplates.notDone", "That didn't go through")}>{serverFindings.message}</Banner>
        ) : null}
        {otherErrors.length ? (
          <Banner tone="danger" size="compact" title={i18nT("journeys.customTemplates.problems", "Problems to fix before publishing")}>
            <ul style={{ margin: 0, paddingLeft: "18px" }}>
              {otherErrors.slice(0, 8).map((f, i) => <li key={i}>{f.message}</li>)}
            </ul>
          </Banner>
        ) : null}
        {allWarnings.length ? (
          <Banner tone="warning" size="compact" title={i18nT("journeys.customTemplates.warnings", "Worth checking")}>
            <ul style={{ margin: 0, paddingLeft: "18px" }}>{allWarnings.slice(0, 8).map((f, i) => <li key={i}>{f.message}</li>)}</ul>
          </Banner>
        ) : null}
        {narrow ? (
          <SegmentedControl full size="sm" value={pane} onChange={(v) => setPane(v as "source" | "preview")}
            ariaLabel={i18nT("journeys.customTemplates.show", "Show")}
            options={[{ value: "source", label: i18nT("journeys.customTemplates.source", "Source") }, { value: "preview", label: i18nT("journeys.customTemplates.preview", "Preview") }]} />
        ) : null}
        <div style={{ display: "grid", gridTemplateColumns: narrow ? "minmax(0, 1fr)" : "minmax(0, 1fr) minmax(0, 1fr)", gap: "16px", minWidth: 0 }}>
          {!narrow || pane === "source" ? sourcePane : null}
          {!narrow || pane === "preview" ? previewPane : null}
        </div>
      </div>
    );
  }

  return (
    <>
      <Modal open={props.open} size="fullscreen" onClose={close} closeOnEscape={!locked}
        title={i18nT("journeys.customTemplates.libraryTitle", "Template library")}
        description={i18nT("journeys.customTemplates.libraryDescription", "Custom HTML pages for event lists and event pages. Save drafts freely; visitors only see a template once it is published and chosen.")}>
        <div style={{ display: "grid", gridTemplateColumns: narrow ? "minmax(0, 1fr)" : "260px minmax(0, 1fr)", gap: "20px", minWidth: 0, alignItems: "start", padding: "0 var(--jrni-space-6) var(--jrni-space-4)", boxSizing: "border-box" }}>
          {!narrow || !draft ? listPane : (
            <div>
              <Button size="sm" hierarchy="tertiary" disabled={locked} onClick={() => guard(resetToIdle)}>
                {i18nT("journeys.customTemplates.allTemplates", "All templates")}
              </Button>
            </div>
          )}
          {editor}
        </div>
      </Modal>

      <SearchPickerDialog open={inserting} onClose={() => setInserting(false)}
        title={i18nT("journeys.customTemplates.insertComponent", "Insert component")}
        description={i18nT("journeys.customTemplates.insertHint", "Adds the component at the cursor. Set its attributes in the source.")}
        items={catalog.map((c) => ({ id: c.tag, title: `<${c.tag}>`, subtitle: c.purpose, searchText: `${c.tag} ${c.purpose}` }))}
        onPick={insertComponent} />

      <Modal open={!!creating} onClose={() => { if (!creating?.checking) setCreating(null); }} title={i18nT("journeys.customTemplates.newTemplate", "New template")} maxWidth={480}>
        {creating ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            <Input label={i18nT("journeys.customTemplates.name", "Name")} value={creating.title} full maxLength={200} disabled={creating.checking}
              onChange={(e) => { const title = e.target.value; setCreating((c) => c && ({ ...c, title, error: undefined })); }} />
            <Input label={i18nT("journeys.customTemplates.address", "Address")} value={creating.slug} full maxLength={120} disabled={creating.checking}
              helperText={i18nT("journeys.customTemplates.addressHint", "Lowercase letters, numbers and hyphens. It can't be changed later.")}
              onChange={(e) => { const slug = e.target.value.toLowerCase(); setCreating((c) => c && ({ ...c, slug, error: undefined })); }} />
            <Field label={i18nT("journeys.customTemplates.kind", "Used for")}>
              <SegmentedControl full value={creating.kind} onChange={(v) => setCreating((c) => c && ({ ...c, kind: v as PortalPageKind }))}
                ariaLabel={i18nT("journeys.customTemplates.kind", "Used for")}
                options={[{ value: "page", label: kindLabel("page") }, { value: "event-detail", label: kindLabel("event-detail") }]} />
            </Field>
            <Input label={i18nT("journeys.customTemplates.defaultLanguageLabel", "Default language")} value={creating.locale} full disabled={creating.checking}
              helperText={i18nT("journeys.customTemplates.addLanguageHint", "A language tag, such as en or es-MX")}
              onChange={(e) => { const locale = e.target.value; setCreating((c) => c && ({ ...c, locale, error: undefined })); }} />
            <TimeZonePicker label={i18nT("journeys.customTemplates.timeZone", "Time zone")} value={creating.timeZone} full disabled={creating.checking}
              onChange={(z) => setCreating((c) => c && ({ ...c, timeZone: z }))} />
            {creating.error ? <Banner tone="danger" size="compact">{creating.error}</Banner> : null}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
              <Button hierarchy="secondary" disabled={creating.checking} onClick={() => setCreating(null)}>{i18nT("journeys.customTemplates.cancel", "Cancel")}</Button>
              <Button hierarchy="primary" disabled={creating.checking} aria-busy={creating.checking || undefined}
                iconLeft={creating.checking ? <Spinner size="sm" onColor label={i18nT("journeys.customTemplates.checking", "Checking")} /> : undefined}
                onClick={() => { void createTemplate(); }}>
                {creating.checking ? i18nT("journeys.customTemplates.checking", "Checking") : i18nT("journeys.customTemplates.create", "Create")}
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog open={!!pendingLeave} onCancel={() => setPendingLeave(null)}
        onConfirm={() => { const next = pendingLeave; setPendingLeave(null); next?.then(); }}
        tone="danger"
        title={i18nT("journeys.customTemplates.discardTitle", "Discard unsaved changes?")}
        body={i18nT("journeys.customTemplates.discardBody", "Your changes to this template haven't been saved.")}
        confirmLabel={i18nT("journeys.customTemplates.discard", "Discard changes")}
        cancelLabel={i18nT("journeys.customTemplates.keepEditing", "Keep editing")} />
    </>
  );
}
